import { User } from "firebase/auth";
import { auth, signInAnonymously } from "./firebase";
import { logInfo } from "./logger";

/**
 * Returns a guaranteed Firebase `User` for the auth-dependent boundaries of the
 * scan flow (the `/bin-predict` upload and the finalize/Firestore writes). If
 * `auth.currentUser` is already set it is returned as-is; otherwise this signs
 * in anonymously EXACTLY ONCE and returns the credential's user.
 *
 * Load-bearing contract — also documented in HERE_BE_DRAGONS.md § "Auth flow":
 *
 *   1. Returns `cred.user` and NEVER re-reads `auth.currentUser` after the
 *      sign-in. On the iOS Safari / in-app WKWebView contexts this guards
 *      against (Failure mode G, runbook `projects/pwa/auth-flow.md`),
 *      `signInAnonymously` can resolve with a valid credential while
 *      `auth.currentUser` stays null. The returned `UserCredential.user` is
 *      valid regardless — a future "cleanup" that simplifies the call sites
 *      back to `auth.currentUser` would silently reintroduce the exact
 *      null-user bug this exists to prevent.
 *
 *   2. ONE attempt only — no loop, no `onAuthStateChanged` listener. If the
 *      single `signInAnonymously` rejects, the error propagates to the
 *      caller's existing failure/UX path. A retry loop / "signout watchdog"
 *      was explicitly rejected: it degenerates into a re-auth storm against a
 *      browser that keeps evicting auth.
 */
export async function ensureAuthedUser(): Promise<User> {
  if (auth.currentUser) return auth.currentUser;
  logInfo("[auth] ensureAuthedUser: currentUser null, signing in anonymously");
  const cred = await signInAnonymously(auth);
  logInfo("[auth] ensureAuthedUser: anon sign-in ok", {
    uidPrefix: cred.user.uid.slice(0, 8),
  });
  return cred.user;
}
